import React, { useEffect, useRef, useState } from "react";
import { ModernContentGrid } from "../FolderView/components/ModernContentGrid";
import { AmpDocumentsSection } from "@/components/AMP/AmpDocumentsSection";

import { ModernActionBar } from "../FolderView/components/ModernActionBar";
import { useProjectRootContent } from "./hooks/useProjectRootContent";
import { useProjectNextStep } from "./hooks/useProjectNextStep";
import { NextStepPanel } from "./components/NextStepPanel";
import StandaloneDocumentView from "./components/StandaloneDocumentView";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, createProjectBreadcrumb } from "@/components/ui/breadcrumb";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BasProcessView from "@/components/BasProcess/BasProcessView";
import DeleteDocumentDialog from "@/components/Documents/DeleteDocumentDialog";
import TrashPanel from "@/components/Documents/TrashPanel";
import AuditLogDialog from "@/components/Documents/AuditLogDialog";
import type { DeletionTarget } from "@/lib/documents/deletion";
import { FolderOpen, FileText, Route, HardHat, Trash2 } from "lucide-react";
import DocumentModeToggle from "./components/DocumentModeToggle";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProjectShareButton from "@/components/Auth/Projects/ProjectShareButton";
import { useAuth } from "@/context/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


interface ProjectRootViewProps {
  projectId: string;
  onSelectFolder: (folderId: string) => void;
  onStartAssessment: (projectId: string, folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  onCreateNewAssessment: (projectId: string, folderId: string) => void;
  refreshFolderTree?: () => void;
}

const ProjectRootView: React.FC<ProjectRootViewProps> = ({
  projectId,
  onSelectFolder,
  onStartAssessment,
  onNavigateToAssessment,
  onCreateNewAssessment,
  refreshFolderTree
}) => {
  const navigate = useNavigate();
  const {
    project,
    folders,
    assessments,
    isLoading,
    error,
    lastUpdatedAt,
    refetch
  } = useProjectRootContent({ projectId });

  const { user } = useAuth();
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);

  const [mainTab, setMainTab] = useState("innehall");

  // Nästa steg och status för projektets grundläggande dokument
  const {
    steps,
    nextStep,
    doneCount,
    total,
    loading: stepsLoading,
    refresh: refreshSteps
  } = useProjectNextStep(projectId);

  // Verktygsblocket är hopfällt tills användaren behöver det
  const toolsRef = useRef<HTMLElement | null>(null);
  const [searchParams] = useSearchParams();
  const toolsParam = searchParams.get("tools");
  const [toolsOpen, setToolsOpen] = useState(() => toolsParam === "basp" || toolsParam === "basu");
  const [toolsTab, setToolsTab] = useState<string>(
    toolsParam === "basp" || toolsParam === "basu" ? toolsParam : "basp"
  );

  useEffect(() => {
    if (toolsParam === "basp" || toolsParam === "basu") {
      requestAnimationFrame(() => {
        toolsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenTools = (phase: "basp" | "basu") => {
    setToolsTab(phase);
    setToolsOpen(true);
    requestAnimationFrame(() => {
      toolsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  // Trädvy: som standard visas bara projektets grundnivå (Allmänt / ingen mapp),
  // mapparna ligger som eget träd under. Växlaren visar allt samlat.
  const [showAllDocuments, setShowAllDocuments] = useState(false);

  const allmantFolderId = React.useMemo(
    () =>
      (folders || []).find(
        (f: any) => !f?.parent_folder_id && String(f?.name || "").toLowerCase() === "allmänt"
      )?.id ?? null,
    [folders]
  );

  const rootAssessments = React.useMemo(
    () =>
      (assessments || []).filter((a: any) => !a.folder_id || a.folder_id === allmantFolderId),
    [assessments, allmantFolderId]
  );

  // Antal och exempelnamn per mapp, används på mappkorten
  const folderStats = React.useMemo(() => {
    const stats: Record<string, { count: number; names: string[] }> = {};
    (assessments || []).forEach((a: any) => {
      if (!a.folder_id) return;
      const entry = stats[a.folder_id] || { count: 0, names: [] };
      entry.count += 1;
      if (entry.names.length < 3 && a.name) entry.names.push(a.name);
      stats[a.folder_id] = entry;
    });
    return stats;
  }, [assessments]);

  const visibleAssessments = showAllDocuments ? assessments : rootAssessments;

  // Dokumentläge: kopplat till AMP eller fristående dokument.
  // Projektet har ett grundläge, och Bas-P respektive Bas-U kan avvika från det.
  const [documentMode, setDocumentMode] = useState<"amp" | "standalone">("amp");
  const [baspMode, setBaspMode] = useState<"amp" | "standalone" | null>(null);
  const [basuMode, setBasuMode] = useState<"amp" | "standalone" | null>(null);
  const [savingMode, setSavingMode] = useState(false);
  const isStandalone = documentMode === "standalone";
  const effectiveBaspMode = baspMode ?? documentMode;
  const effectiveBasuMode = basuMode ?? documentMode;

  const parseMode = (value: unknown): "amp" | "standalone" | null =>
    value === "amp" || value === "standalone" ? value : null;

  useEffect(() => {
    const p = project as any;
    if (!p) return;
    setDocumentMode(parseMode(p.document_mode) ?? "amp");
    setBaspMode(parseMode(p.document_mode_basp));
    setBasuMode(parseMode(p.document_mode_basu));
  }, [project]);

  const persistMode = async (
    column: "document_mode" | "document_mode_basp" | "document_mode_basu",
    value: "amp" | "standalone" | null,
    rollback: () => void
  ) => {
    setSavingMode(true);
    const { error } = await supabase
      .from("projects")
      .update({ [column]: value } as any)
      .eq("id", projectId);
    setSavingMode(false);
    if (error) {
      rollback();
      toast.error("Kunde inte spara dokumentläget");
      return;
    }
    toast.success(
      value === null
        ? "Följer projektets läge"
        : value === "amp"
          ? "Dokumenten kopplas till arbetsmiljöplanen"
          : "Dokumenten används fristående"
    );
    refetch();
  };

  const handleChangeMode = async (mode: "amp" | "standalone") => {
    if (mode === documentMode || savingMode) return;
    const previous = documentMode;
    setDocumentMode(mode);
    await persistMode("document_mode", mode, () => setDocumentMode(previous));
  };

  const handleChangeBaspMode = async (mode: "amp" | "standalone" | null) => {
    if (mode === baspMode || savingMode) return;
    const previous = baspMode;
    setBaspMode(mode);
    await persistMode("document_mode_basp", mode, () => setBaspMode(previous));
  };

  const handleChangeBasuMode = async (mode: "amp" | "standalone" | null) => {
    if (mode === basuMode || savingMode) return;
    const previous = basuMode;
    setBasuMode(mode);
    await persistMode("document_mode_basu", mode, () => setBasuMode(previous));
  };


  // State for creating a new risk assessment from project root
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedFolderForNew, setSelectedFolderForNew] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Deletion confirmation state
  const [pendingDelete, setPendingDelete] = useState<{ kind: 'folder' | 'assessment'; id: string; name: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeletionTarget | null>(null);
  const [deleting, setDeleting] = useState(false);


  // Basic permission handling: owner or accepted collaborator
  const isOwner = !!(project && user && (project as any).user_id === user.id);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const canCreate = isOwner || isCollaborator;

  useEffect(() => {
    const checkPendingShare = async () => {
      if (!user || project || isLoading) return;
      const orFilters = [
        `and(shared_with_email.eq.${JSON.stringify(user.email)},accepted.is.null)`,
        `and(shared_with_email.eq.${JSON.stringify(user.email)},accepted.eq.false)`,
        `and(shared_with_id.eq.${user.id},accepted.is.null)`,
        `and(shared_with_id.eq.${user.id},accepted.eq.false)`
      ].join(",");
      const { data, error } = await supabase
        .from("project_shares")
        .select("id")
        .eq("project_id", projectId)
        .or(orFilters)
        .limit(1);
      if (!error && data && data.length > 0) {
        setPendingShareId(data[0].id);
      } else {
        setPendingShareId(null);
      }
    };
    checkPendingShare();
  }, [user, project, isLoading, projectId]);

  // Determine if current user is an accepted collaborator on this project
  useEffect(() => {
    const checkCollaborator = async () => {
      if (!user || !projectId) return;
      const orFilters = [
        `shared_with_id.eq.${user.id}`,
        user.email ? `shared_with_email.eq.${JSON.stringify(user.email)}` : null
      ].filter(Boolean).join(",");

      const { data, error } = await supabase
        .from("project_shares")
        .select("id, accepted")
        .eq("project_id", projectId)
        .or(orFilters)
        .eq("accepted", true)
        .limit(1);

      setIsCollaborator(!error && !!data && data.length > 0);
    };

    checkCollaborator();
  }, [user, projectId]);

  const handleAcceptInline = async () => {
    if (!pendingShareId || !user?.id) return;
    const { error } = await supabase
      .from("project_shares")
      .update({ accepted: true, shared_with_id: user.id })
      .eq("id", pendingShareId);
    if (error) {
      toast.error("Kunde inte acceptera delningen");
      return;
    }
    toast.success("Delningen accepterades");
    setPendingShareId(null);
    await refetch();
  };

  const handleRejectInline = async () => {
    if (!pendingShareId) return;
    const { error } = await supabase
      .from("project_shares")
      .delete()
      .eq("id", pendingShareId);
    if (error) {
      toast.error("Kunde inte avvisa delningen");
      return;
    }
    toast.success("Delningen avvisades");
    setPendingShareId(null);
    navigate('/uppdrag');
  };
  const handleNavigateToSharedFolders = () => {
    navigate('/uppdrag');
    // Small delay to ensure navigation completes before setting active tab
    setTimeout(() => {
      const event = new CustomEvent('setActiveSubTab', { detail: 'shared-folders' });
      window.dispatchEvent(event);
    }, 100);
  };

  // Create assessment flow from project root
  const openCreateAssessmentDialog = () => {
    setSelectedFolderForNew(null);
    setIsCreateDialogOpen(true);
  };

  const findExistingDefaultFolder = (): string | null => {
    const defaultFolder = folders?.find((f: any) => f?.name?.toLowerCase() === 'allmänt' && !f?.parent_folder_id);
    return defaultFolder?.id ?? null;
  };

  const createDefaultFolder = async (): Promise<string | null> => {
    try {
      if (!user) {
        toast.error("Du måste vara inloggad för att skapa mappar");
        return null;
      }
      const { data, error } = await supabase
        .from('folders')
        .insert({
          name: 'Allmänt',
          description: null,
          project_id: projectId,
          parent_folder_id: null,
          created_by: user.id
        })
        .select('id')
        .single();

      if (error) throw error;

      toast.success('Mappen "Allmänt" har skapats!');
      await refetch();
      refreshFolderTree?.();
      return data?.id as string;
    } catch (e: any) {
      console.error('Error creating default folder:', e);
      toast.error('Kunde inte skapa standardmappen', {
        description: e?.message || 'Ett oväntat fel uppstod'
      });
      return null;
    }
  };

  // Folders are optional: content created at project level lands in a default folder
  const ensureProjectFolderId = async (): Promise<string | null> => {
    return findExistingDefaultFolder() || (await createDefaultFolder());
  };

  const handleConfirmNewAssessment = async () => {
    if (!projectId) return;
    setCreating(true);
    try {
      let folderId = selectedFolderForNew;
      if (!folderId) {
        folderId = findExistingDefaultFolder() || await createDefaultFolder();
      }
      if (folderId) {
        onCreateNewAssessment(projectId, folderId);
        setIsCreateDialogOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCancelNewAssessment = () => {
    setIsCreateDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Laddar projektinnehåll...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Card className="p-6 max-w-md text-center">
          <p className="text-destructive mb-4">Kunde inte ladda projektinnehåll</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (!project && pendingShareId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Card className="p-6 max-w-md text-center">
          <h3 className="text-lg font-semibold mb-2">Delning väntar på ditt godkännande</h3>
          <p className="text-sm text-muted-foreground mb-4">Acceptera delningen för att se projektet.</p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={handleAcceptInline} className="bg-green-600 hover:bg-green-700">Acceptera</Button>
            <Button variant="outline" onClick={handleRejectInline} className="border-red-300 text-red-700 hover:bg-red-50">Avvisa</Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleOpenFolder = async (folderId: string): Promise<void> => {
    onSelectFolder(folderId);
  };

  const handleCreateFolder = async (name: string, description?: string): Promise<void> => {
    try {
      if (!user) {
        toast.error("Du måste vara inloggad för att skapa mappar");
        return;
      }
      const { data, error } = await supabase
        .from('folders')
        .insert({
          name: name.trim(),
          description: description ?? null,
          project_id: projectId,
          parent_folder_id: null,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(`Mapp "${name}" har skapats!`);
      await refetch();
      refreshFolderTree?.();
    } catch (e: any) {
      console.error('Error creating root folder:', e);
      toast.error('Kunde inte skapa mappen', {
        description: e?.message || 'Ett oväntat fel uppstod'
      });
    }
  };
  const handleDeleteFolder = async (folderId: string): Promise<void> => {
    const folder = (folders || []).find((f: any) => f.id === folderId);
    setPendingDelete({ kind: 'folder', id: folderId, name: folder?.name || 'Mappen' });
  };

  const handleDeleteAssessment = async (assessmentId: string): Promise<void> => {
    const item = (assessments || []).find((a: any) => a.id === assessmentId);
    if (!item) return;
    // Rätt tabell används via dokumentets typ – inte alltid risksammanställning
    setDeleteTarget({
      type: (item.type as DeletionTarget['type']) || 'risk_assessment',
      id: assessmentId,
      name: item.name || 'Dokumentet',
      projectId,
    });
  };

  const performDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      if (pendingDelete.kind === 'folder') {
        const { data: subfolders, error: subErr } = await supabase
          .from('folders')
          .select('id')
          .eq('parent_folder_id', pendingDelete.id);
        if (subErr) throw subErr;
        if (subfolders && subfolders.length > 0) {
          toast.error('Ta bort undermapparna först');
          return;
        }

        const { error: assessErr } = await supabase
          .from('folder_risk_assessments')
          .delete()
          .eq('folder_id', pendingDelete.id);
        if (assessErr) throw assessErr;

        const { error } = await supabase.from('folders').delete().eq('id', pendingDelete.id);
        if (error) throw error;
        toast.success('Mapp borttagen');
      }

      setPendingDelete(null);
      await refetch();
      refreshFolderTree?.();
    } catch (e: any) {
      console.error('Delete failed:', e);
      toast.error(e?.message || 'Kunde inte ta bort');
    } finally {
      setDeleting(false);
    }
  };


  const handleNavigateToAssessmentWithFolder = (assessmentId: string) => {
    // Använd dokumentets faktiska mapp så att länken alltid blir giltig
    const match = (assessments || []).find(
      (a: any) => a.assessment_id === assessmentId || a.id === assessmentId
    );
    const targetFolderId = (match as any)?.folder_id;
    if (!targetFolderId) {
      toast.error("Kunde inte öppna dokumentet", {
        description: "Dokumentet saknar koppling till en mapp.",
      });
      return;
    }
    onNavigateToAssessment(match?.assessment_id || assessmentId, targetFolderId);
  };

  const handleStartAssessmentForProject = () => {
    // For starting assessment at project level, we might need a default folder
    // or handle this differently based on business logic
    console.log("Start assessment for project:", projectId);
  };

  // Enskilt dokument: visa en minimal vy utan AMP, mappar och processflikar
  if ((project as any)?.mode === "standalone") {
    return (
      <StandaloneDocumentView
        projectId={projectId}
        project={project}
        documents={assessments || []}
        onConnectToAmp={async () => {
          const { error: modeError } = await supabase
            .from("projects")
            .update({ mode: "project", document_mode: "amp" } as any)
            .eq("id", projectId);
          if (modeError) {
            toast.error("Kunde inte koppla till arbetsmiljöplanen");
            return;
          }
          toast.success("Dokumentet ingår nu i ett projekt med arbetsmiljöplan");
          await refetch();
        }}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Project Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Breadcrumb items={createProjectBreadcrumb(project?.name || "Projekt")} />
              {project?.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {project.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-wrap">
              {canCreate && (
                <DocumentModeToggle
                  value={documentMode}
                  onChange={(m) => m && handleChangeMode(m)}
                  disabled={savingMode}
                />
              )}

              {canCreate && (
                <Button size="sm" onClick={openCreateAssessmentDialog} className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Ny risksammanställning
                </Button>
              )}
              <ProjectShareButton 
                projectId={projectId} 
                projectName={project?.name || "Projekt"} 
              />
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToSharedFolders}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <FolderOpen className="h-4 w-4" />
                Se delade mappar
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={mainTab} onValueChange={setMainTab} className="flex-1 flex flex-col">
        <div className="container max-w-7xl mx-auto px-6 pt-4">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="innehall" className="gap-2">
              <FolderOpen className="h-4 w-4" /> Översikt
            </TabsTrigger>
            <TabsTrigger value="bas-p" className="gap-2">
              <FileText className="h-4 w-4" /> Bas-P
            </TabsTrigger>
            <TabsTrigger value="bas-u" className="gap-2">
              <HardHat className="h-4 w-4" /> Bas-U
            </TabsTrigger>
            <TabsTrigger value="bas-processen" className="gap-2">
              <Route className="h-4 w-4" /> Bas-processen
            </TabsTrigger>
            <TabsTrigger value="papperskorg" className="gap-2">
              <Trash2 className="h-4 w-4" /> Papperskorg
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="innehall" className="mt-0 flex-1 flex flex-col">
          {/* Nästa steg – ett tydligt fokus åt gången */}
          <section className="container max-w-7xl mx-auto px-4 sm:px-6 pt-6">
            <NextStepPanel
              steps={steps}
              nextStep={nextStep}
              doneCount={doneCount}
              total={total}
              loading={stepsLoading}
              onOpenTools={handleOpenTools}
            />
          </section>

          {/* Huvuddokument: AMP */}
          {!isStandalone && (
            <section className="container max-w-7xl mx-auto px-4 sm:px-6 pt-8">
              <AmpDocumentsSection
                projectId={projectId}
                folderId={null}
                ensureFolderId={ensureProjectFolderId}
              />
            </section>
          )}

          {/* Avdelare */}
          <div className="container max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-6">
            <div className="flex items-center gap-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap">
                Verktyg och innehåll
              </span>
              <div className="h-px flex-1 bg-border/60" />
            </div>
          </div>

          {/* Action Bar */}
          <section ref={toolsRef} className="container max-w-7xl mx-auto px-4 sm:px-6">
            <ModernActionBar

              onCreateFolder={handleCreateFolder}
              projectId={projectId}
              folderId="" // Empty string for project root
              isShared={false}
              ensureFolderId={ensureProjectFolderId}
              onRefresh={() => { refetch(); refreshFolderTree?.(); refreshSteps(); }}
              mode={documentMode}
              collapsible
              open={toolsOpen}
              onOpenChange={setToolsOpen}
              activeTab={toolsTab}
              onActiveTabChange={setToolsTab}
            />
          </section>


          {/* Content Grid */}
          <div className="flex-1 container max-w-7xl mx-auto px-4 sm:px-6 py-6">
            <ModernContentGrid
              folderData={null} // No parent folder for project root
              subfolders={folders}
              assessments={visibleAssessments}
              folderStats={folderStats}
              showAllDocuments={showAllDocuments}
              onToggleShowAll={setShowAllDocuments}
              projectId={projectId}
              folderId={null} // No specific folder for project root
              isShared={false}
              onOpenFolder={handleOpenFolder}
              onNavigateToAssessment={handleNavigateToAssessmentWithFolder}
              onDeleteFolder={handleDeleteFolder}
              onDeleteAssessment={handleDeleteAssessment}
              onCreateFolder={handleCreateFolder}
              onStartAssessment={handleStartAssessmentForProject}
              ensureFolderId={ensureProjectFolderId}
              onRefresh={() => { refetch(); refreshFolderTree?.(); }}
              standalone={isStandalone}
            />
          </div>
        </TabsContent>

        <TabsContent value="bas-p" className="mt-0 flex-1 flex flex-col">
          {canCreate && (
            <div className="container max-w-7xl mx-auto px-6 pt-6">
              <DocumentModeToggle
                label="Läge för Bas-P"
                value={baspMode}
                onChange={handleChangeBaspMode}
                disabled={savingMode}
                allowInherit
                inheritedMode={documentMode}
              />
            </div>
          )}
          <section className="container max-w-7xl mx-auto px-6 pt-6">

            <ModernActionBar
              onCreateFolder={handleCreateFolder}
              projectId={projectId}
              folderId=""
              isShared={false}
              ensureFolderId={ensureProjectFolderId}
              onRefresh={() => { refetch(); refreshFolderTree?.(); }}
              groups={["amp", "basp"]}
              mode={effectiveBaspMode}
              heading="Bas-P · Projektering och underlag"
              headingDescription="Dokument som byggarbetsmiljösamordnaren för planering och projektering ansvarar för"
            />
          </section>
          <div className="flex-1 container max-w-7xl mx-auto px-6 py-2">
            <ModernContentGrid
              folderData={null}
              subfolders={folders}
              assessments={visibleAssessments}
              folderStats={folderStats}
              showAllDocuments={showAllDocuments}
              onToggleShowAll={setShowAllDocuments}
              projectId={projectId}
              folderId={null}
              isShared={false}
              onOpenFolder={handleOpenFolder}
              onNavigateToAssessment={handleNavigateToAssessmentWithFolder}
              onDeleteFolder={handleDeleteFolder}
              onDeleteAssessment={handleDeleteAssessment}
              onCreateFolder={handleCreateFolder}
              onStartAssessment={handleStartAssessmentForProject}
              ensureFolderId={ensureProjectFolderId}
              onRefresh={() => { refetch(); refreshFolderTree?.(); }}
              only="basp"
              standalone={effectiveBaspMode === "standalone"}

            />
          </div>
        </TabsContent>

        <TabsContent value="bas-u" className="mt-0 flex-1 flex flex-col">
          {canCreate && (
            <div className="container max-w-7xl mx-auto px-6 pt-6">
              <DocumentModeToggle
                label="Läge för Bas-U"
                value={basuMode}
                onChange={handleChangeBasuMode}
                disabled={savingMode}
                allowInherit
                inheritedMode={documentMode}
              />
            </div>
          )}
          <section className="container max-w-7xl mx-auto px-6 pt-6">

            <ModernActionBar
              onCreateFolder={handleCreateFolder}
              projectId={projectId}
              folderId=""
              isShared={false}
              ensureFolderId={ensureProjectFolderId}
              onRefresh={() => { refetch(); refreshFolderTree?.(); }}
              groups={["basu", "gemensamt"]}
              mode={effectiveBasuMode}
              heading="Bas-U · Utförande på arbetsplatsen"
              headingDescription="Arbetsberedningar, skedesplan, samordningsmöten och projektledardokument"
            />
          </section>
          <div className="flex-1 container max-w-7xl mx-auto px-6 py-2">
            <ModernContentGrid
              folderData={null}
              subfolders={folders}
              assessments={visibleAssessments}
              folderStats={folderStats}
              showAllDocuments={showAllDocuments}
              onToggleShowAll={setShowAllDocuments}
              projectId={projectId}
              folderId={null}
              isShared={false}
              onOpenFolder={handleOpenFolder}
              onNavigateToAssessment={handleNavigateToAssessmentWithFolder}
              onDeleteFolder={handleDeleteFolder}
              onDeleteAssessment={handleDeleteAssessment}
              onCreateFolder={handleCreateFolder}
              onStartAssessment={handleStartAssessmentForProject}
              ensureFolderId={ensureProjectFolderId}
              onRefresh={() => { refetch(); refreshFolderTree?.(); }}
              only="basu"
              standalone={effectiveBasuMode === "standalone"}

            />
          </div>
        </TabsContent>

        <TabsContent value="bas-processen" className="mt-0 flex-1">
          <div className="container max-w-7xl mx-auto px-6 py-6">
            <BasProcessView projectId={projectId} />
          </div>
        </TabsContent>
        <TabsContent value="papperskorg" className="mt-0 flex-1">
          <section className="container max-w-7xl mx-auto px-4 sm:px-6 py-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Borttagna dokument ligger kvar här tills de raderas permanent. Du kan titta på och
                återställa tidigare versioner innan dess.
                {lastUpdatedAt &&
                  ` · Listan uppdaterades ${lastUpdatedAt.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}`}
              </p>
              <AuditLogDialog
                target={{ projectId, title: "Ändringslogg för projektet" }}
                buttonLabel="Ändringslogg för projektet"
              />
            </div>
            <TrashPanel projectId={projectId} onChanged={refetch} />
          </section>
        </TabsContent>
      </Tabs>


      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ny risksammanställning</DialogTitle>
            <DialogDescription>Välj en mapp eller skapa i standardmappen "Allmänt".</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {folders && folders.length > 0 ? (
              <div className="max-h-60 overflow-auto rounded-md border">
                {folders.map((f: any) => (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFolderForNew(f.id)}
                    className={`w-full text-left px-3 py-2 hover:bg-accent ${selectedFolderForNew === f.id ? 'bg-accent' : ''}`}
                  >
                    <div className="font-medium">{f.name}</div>
                    {f.description && <div className="text-sm text-muted-foreground">{f.description}</div>}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Inga mappar hittades i projektet.</p>
            )}

            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setSelectedFolderForNew(null)}>
                Skapa i "Allmänt"
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={handleCancelNewAssessment}>Avbryt</Button>
                <Button onClick={handleConfirmNewAssessment} disabled={creating}>
                  {creating ? 'Skapar...' : 'Fortsätt'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteDocumentDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        target={deleteTarget}
        onDeleted={async () => { setDeleteTarget(null); await refetch(); refreshFolderTree?.(); }}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.kind === 'folder' ? 'Ta bort mapp?' : 'Ta bort dokument?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === 'folder'
                ? `"${pendingDelete?.name}" och alla dokument i mappen tas bort permanent. Undermappar måste tas bort först.`
                : `"${pendingDelete?.name}" tas bort permanent och kan inte återställas.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); performDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Tar bort...' : 'Ta bort'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ProjectRootView;